import React from 'react';
import PropTypes from 'prop-types';
import { localPoint } from '@visx/event';
import Pie from '@visx/shape/lib/shapes/Pie';
import { Group } from '@visx/group';
import { withTooltip, TooltipWithBounds } from '@visx/tooltip';
import { Text } from '@visx/text';
import { useTheme } from '@material-ui/core';
import { noop } from 'Helpers/PropTypes';
import AnimatedPie from '../Snapshot/Holdings/Visualization/AnimatedPie';

// accessor function
const getValue = d => parseFloat(d.value);

const getChartLeftPos = (align, centerX, margin) => {
  switch (align) {
    case 'left': {
      return centerX - margin.left;
    }
    default: {
      return centerX;
    }
  }
};
function RingWithText({
  data,
  width,
  height,
  margin,
  thickness,
  ringPadding,
  textFields,
  textOffset,
  animate,
  holdings,
  showTooltip,
  hideTooltip,
  tooltipLeft,
  tooltipTop,
  tooltipOpen,
  tooltipData,
  title,
  titleProps,
  align,
  onSegmentClick,
}) {
  const theme = useTheme();

  const handleMouseOver = React.useCallback(
    (event) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      const { tooltip } = event.target.dataset;
      if (tooltip) {
        showTooltip({
          tooltipLeft: coords.x,
          tooltipTop: coords.y,
          tooltipData: tooltip,
        });
      } else {
        hideTooltip();
      }
    },
    [showTooltip],
  );

  React.useEffect(() => {
    if (tooltipOpen) hideTooltip();
  }, []);

  if (width < 10 || !holdings) return null;

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;

  return (
    <>
      {tooltipOpen && (
        <TooltipWithBounds
          // set this to random so it correctly updates with parent bounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
        >
          <p
            style={{
              whiteSpace: 'pre',
              textAlign: 'left',
            }}
          >
            {tooltipData}
          </p>
        </TooltipWithBounds>
      )}
      <svg width={width} height={height}>
        <Group
          top={centerY + margin.top}
          left={getChartLeftPos(align, centerX, margin)}
        >
          <Pie
            data={data}
            pieValue={getValue}
            innerRadius={radius - ringPadding - thickness}
            outerRadius={radius - ringPadding}
            pieSort={null}
            pieSortValues={null}
          >
            {pie => (
              <AnimatedPie
                {...pie}
                showTooltip={handleMouseOver}
                hideTooltip={() => hideTooltip()}
                animate={animate}
                onClickDatum={onSegmentClick}
                getKey={({ data: { label } }) => label}
                getColor={({ data: { color } }) => color}
              />
            )}
          </Pie>
          <Text
            y={-16}
            style={{ fontSize: '16px' }}
            width="100%"
            textAnchor="middle"
            fill={theme.palette.primary.main}
            {...titleProps}
          >
            {title}
          </Text>
          {textFields.map(({ text, props = {} }, index) => {
            return (
              <Text
                key={text}
                y={index * textOffset}
                width={width}
                textAnchor="middle"
                {...props}
              >
                {text}
              </Text>
            );
          })}
        </Group>
      </svg>
    </>
  );
}

export default withTooltip(RingWithText);

RingWithText.defaultProps = {
  margin: {
    top: 8,
    right: 8,
    bottom: 8,
    left: 8,
  },
  animate: true,
  thickness: 16,
  ringPadding: 12,
  title: '',
  textFields: [],
  textOffset: 16,
  align: 'left',
  titleProps: {},
  onSegmentClick: noop,
};

RingWithText.propTypes = {
  data: PropTypes.array.isRequired,
  textOffset: PropTypes.array,
  textFields: PropTypes.array,
  title: PropTypes.string.isRequired,
  titleProps: PropTypes.object,
  align: PropTypes.oneOf(['left', 'center']).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.object.isRequired,
  margin: PropTypes.number,
  animate: PropTypes.bool,
  tooltipData: PropTypes.node.isRequired,
  tooltipLeft: PropTypes.number.isRequired,
  tooltipTop: PropTypes.number.isRequired,
  tooltipOpen: PropTypes.bool.isRequired,
  hideTooltip: PropTypes.func.isRequired,
  onSegmentClick: PropTypes.func,
};
